import { FunctionComponent, memo, useCallback, useMemo, useState } from "react";
import { IProps } from "./filter-fields.types";
import styles from "./filter-fields.module.scss";
import { FieldFactory } from "../field-factory";
import { faPlus } from "@fortawesome/pro-solid-svg-icons/faPlus";
import { IFilter } from "../../filters.types";
import { getFilterName, getNotVisibleFields, getVisibleFields } from "../../filters.utilities";
import classNames from "classnames";
import { useFormikContext } from "formik";
import { AutoMenu } from "../../../../auto-menu";
import { ActionIcon } from "../../../../action-icon";
import { Icon } from "../../../../icon";
import { Box } from "../../../../box";

/**
 * Component for managing filter fields.
 * @param {object[]} props.visibleFields - The array of visible filter configurations.
 * @param {boolean} props.shouldShowAddFiltersButton - Flag to show the add filters button.
 * @param {string[]} props.fixedFilters - The fixed filter IDs.
 * @param {function} props.onRemove - Function to remove a filter.
 * @param {function} props.onSelect - Function to select a filter.
 * @param {object[]} props.selectItems - The array of filter configurations to select from.
 * @returns {JSX.Element} The filter fields component.
 */
const FilterFields: FunctionComponent<IProps> = ({
    filters,
    visibleFilters: visibleFiltersProp,
    fixedFilters,
    className,
    style,
    children,
    locked,
    gridType = "extended",
    activable,
}) => {
    const { setFieldValue } = useFormikContext();
    const [visibleFilterIds, setVisibleFilterIds] = useState<string[]>(visibleFiltersProp);
    const visibleFields = useMemo<IFilter[]>(
        () => getVisibleFields(visibleFilterIds, filters),
        [visibleFilterIds, filters]
    );
    const notVisible = useMemo(() => getNotVisibleFields(filters, visibleFilterIds), [filters, visibleFilterIds]);
    const shouldShowAddFiltersButton = useMemo(
        () => visibleFilterIds.length < filters.length,
        [visibleFilterIds, filters]
    );
    const handleAddField = useCallback((id: string) => {
        setVisibleFilterIds((visibleFilters) => [...visibleFilters, id]);
    }, []);
    const handleRemoveField = useCallback((id: string) => {
        setVisibleFilterIds((visibleFilters) => [...visibleFilters.filter((visibleId) => visibleId !== id)]);
    }, []);

    return (
        <div
            className={classNames(styles["filter-fields"], className, {
                [styles["filter-fields--compact"]]: gridType === "compact",
                [styles["filter-fields--extended"]]: gridType === "extended",
                [styles["filter-fields--mini"]]: gridType === "mini",
            })}
            style={style}
        >
            {visibleFields?.map((filter, index) => (
                <FieldFactory
                    canBeRemoved={!fixedFilters?.includes(filter.id)}
                    onRemove={() => {
                        setFieldValue(getFilterName(filter), undefined);
                        handleRemoveField(filter.id);
                    }}
                    key={`field-${index}`}
                    field={filter}
                    activable
                />
            ))}
            {!locked && shouldShowAddFiltersButton && (
                <Box>
                    <AutoMenu.WithPlainItems
                        searchable
                        data-testid="filters-add-filter"
                        getLabel={({ label }) => label}
                        getValue={({ id }) => id}
                        onClick={handleAddField}
                        items={notVisible}
                    >
                        <ActionIcon variant="default" size="input-sm">
                            <Icon icon={faPlus} />
                        </ActionIcon>
                    </AutoMenu.WithPlainItems>
                </Box>
            )}
            {children}
        </div>
    );
};

export default memo(FilterFields);
